import { usePrivy } from "@privy-io/react-auth";
import { useAccount, usePublicClient, useConnect } from "wagmi";
import {
  createContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { ethers, Contract, type BigNumberish } from "ethers";
import { toast, type ToastOptions } from "react-toastify";
import { chains } from "@/utils/chains";
import { completeTransaction } from "@/services/misc.service";
import { chainIdToHex } from "@/utils/helpers/chainId-to-hex";
import { handleTransactionError } from "@/utils/helpers/transaction-error";
import { initializeChainColors, updateChainColors } from "@/utils/chain-colors";
import { deleteAuthToken } from "@/utils/axios";

export const WalletContext = createContext<WalletContextType | undefined>(
  undefined,
);

export const WalletProvider = ({ children }: WalletProviderProps) => {
  const { login: privyLogin } = usePrivy();
  const { connect, connectors } = useConnect();
  const {
    address: wagmiAddress,
    chainId: wagmiChainId,
    isConnected: wagmiIsConnected,
  } = useAccount();
  const publicClient = usePublicClient();
  const previousAddressRef = useRef<string | undefined>(undefined);
  const isConnectingRef = useRef(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingPointsRef = useRef(false);
  const lastUpdatePointsTimeRef = useRef(0);
  const providerRef = useRef<
    ethers.BrowserProvider | ethers.JsonRpcProvider | null
  >(null);
  const chainIdRef = useRef<number | undefined>(undefined);
  const address = wagmiAddress;
  const [effectiveChainId, setEffectiveChainId] = useState<number | undefined>(
    wagmiChainId,
  );
  const chainId = effectiveChainId;
  const ethersProvider = useMemo<
    ethers.BrowserProvider | ethers.JsonRpcProvider | null
  >(() => {
    try {
      if (typeof window !== "undefined" && (window as any).ethereum) {
        return new ethers.BrowserProvider((window as any).ethereum, "any");
      }
      if (!publicClient) return null;
      const rpcUrl = (publicClient.transport as any)?.url;
      if (!rpcUrl) return null;
      return new ethers.JsonRpcProvider(rpcUrl);
    } catch (error) {
      console.error("Error creating provider:", error);
      return null;
    }
  }, [publicClient]);

  const isConnected = wagmiIsConnected && !!address;

  useEffect(() => {
    providerRef.current = ethersProvider;
  }, [ethersProvider]);

  useEffect(() => {
    chainIdRef.current = chainId;
  }, [chainId]);

  useEffect(() => {
    setEffectiveChainId(wagmiChainId);
  }, [wagmiChainId]);

  useEffect(() => {
    const eth = typeof window !== "undefined" ? (window as any).ethereum : null;
    if (!eth?.request) return;

    let active = true;

    const sync = async () => {
      try {
        const hex = await eth.request({ method: "eth_chainId" });
        const next = typeof hex === "string" ? Number(hex) : undefined;
        if (!active || !next || Number.isNaN(next)) return;
        chainIdRef.current = next;
        setEffectiveChainId(next);
        updateChainColors(next);
      } catch {}
    };

    const onChainChanged = (hex: string) => {
      const next = Number(hex);
      if (!next || Number.isNaN(next)) return;
      chainIdRef.current = next;
      setEffectiveChainId(next);
      updateChainColors(next);
    };

    sync();
    eth.on?.("chainChanged", onChainChanged);
    return () => {
      active = false;
      eth.removeListener?.("chainChanged", onChainChanged);
    };
  }, [ethersProvider]);

  const safeOpen = useCallback(
    async (options?: any) => {
      if (isConnectingRef.current) {
        console.warn(
          "⚠️ Wallet login already in progress - skipping duplicate request",
        );
        return;
      }
      try {
        isConnectingRef.current = true;

        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
        }

        connectionTimeoutRef.current = setTimeout(() => {
          isConnectingRef.current = false;
          console.warn("⚠️ Wallet login timeout - resetting state");
        }, 30000);

        const usePrivy = import.meta.env.VITE_USE_PRIVY === "true";

        if (usePrivy) {
          // If Privy is primary, try it first
          console.log("🔐 Attempting to connect with Privy...");
          try {
            await privyLogin(options);
            return;
          } catch (privyError) {
            console.warn("⚠️ Privy connection failed:", privyError);
            if (connectors?.length === 0) return;
            // Use the first available connector (e.g., MetaMask)
            console.log("🌈 Falling back to Rainbow Kit connector...");
            connect({ connector: connectors[0] });
            // Note: wagmi's connect() is async but doesn't return a promise
            // Connection happens through the hook state update
            return;
          }
        } else {
          // Try Rainbow Kit first (primary wallet connector)
          if (connectors && connectors.length > 0) {
            try {
              console.log("🌈 Attempting to connect with Rainbow Kit...");
              // Try MetaMask first if available
              const metaMaskConnector = connectors.find(
                (c) => c.id === "metaMask",
              );
              if (metaMaskConnector) {
                console.log(
                  "📱 Found MetaMask connector, initiating connection...",
                );
                connect({ connector: metaMaskConnector });
                return;
              }

              // Otherwise try first available connector
              if (connectors.length > 0) {
                console.log(
                  `📱 Trying connector: ${connectors[0].name || connectors[0].id}`,
                );
                connect({ connector: connectors[0] });
                return;
              }
            } catch (rainbowError) {
              console.warn("⚠️ Rainbow Kit connection failed:", rainbowError);
            }
          }

          // Fallback to Privy if Rainbow Kit fails
          console.log("🔐 Falling back to Privy for wallet connection...");
          await privyLogin(options);
        }
      } catch (error) {
        console.error("Wallet connection error:", error);
        toast.error("Failed to connect wallet. Please try again.");
        throw error;
      } finally {
        isConnectingRef.current = false;
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
      }
    },
    [connectors, connect, privyLogin],
  );

  // Chain colors setup
  useEffect(() => {
    if (chainId) {
      updateChainColors(String(chainId));
    } else {
      initializeChainColors();
    }
  }, [chainId]);

  useEffect(() => {
    initializeChainColors(chainId ? String(chainId) : undefined);
  }, []);

  // Handle address change
  useEffect(() => {
    if (previousAddressRef.current === undefined && address) {
      previousAddressRef.current = address;
      return;
    }

    if (
      previousAddressRef.current &&
      address &&
      previousAddressRef.current !== address
    ) {
      deleteAuthToken();
      localStorage.removeItem("multiplier");
      previousAddressRef.current = address;
      toast.info("Account changed. Please login with the new account.");
      window.location.href = "/";
    } else if (address) {
      previousAddressRef.current = address;
    }
  }, [address]);

  const getChainConfig = useCallback((chainId?: number | string) => {
    const targetChainId = chainId
      ? String(chainId)
      : String(chainIdRef.current);
    const currentChainConfig = chains.find(
      (item) => String(item.id) === targetChainId,
    );
    if (!currentChainConfig) {
      console.warn("No configuration found for chain");
      return null;
    }
    return currentChainConfig;
  }, []);

  const switchChain = useCallback(
    async (id: BigNumberish): Promise<string | null> => {
      const currentProvider = providerRef.current;
      if (!currentProvider) {
        await safeOpen();
        return null;
      }

      const targetChainId = String(id);
      const targetChain = chains.find(
        (item) => String(item.id) === targetChainId,
      );

      if (!targetChain) {
        toast.error(
          `Invalid chain ID '${targetChainId}' specified for switching.`,
        );
        throw new Error(`Invalid chain id '${targetChainId}`);
      }

      try {
        await (currentProvider as any).send("wallet_switchEthereumChain", [
          { chainId: chainIdToHex(Number(id)) },
        ]);
        return targetChainId;
      } catch (switchError: any) {
        // Parse error code from various places where ethers might put it
        let errorCode: number | null = null;

        // Check direct code
        if (typeof switchError.code === "number") {
          errorCode = switchError.code;
        }
        // Check nested error code
        else if (switchError.error?.code) {
          errorCode = switchError.error.code;
        }
        // Check in cause
        else if (switchError.cause?.code) {
          errorCode = switchError.cause.code;
        }
        // Check message for 4902
        else if (switchError.message?.includes("4902")) {
          errorCode = 4902;
        }
        // Check message for "Unrecognized chain ID"
        else if (switchError.message?.includes("Unrecognized chain ID")) {
          errorCode = 4902;
        }

        console.log(
          "Switch error code:",
          errorCode,
          "Error object:",
          switchError,
          "Full error message:",
          switchError.message,
        );

        if (errorCode === 4902) {
          try {
            console.log(
              "Chain not found in wallet, adding chain...",
              targetChain.name,
            );
            await (currentProvider as any).send("wallet_addEthereumChain", [
              {
                chainId: chainIdToHex(Number(id)),
                chainName: targetChain.name,
                rpcUrls: targetChain.rpcUrls,
                blockExplorerUrls: targetChain.blockExplorerUrls || null,
                nativeCurrency: targetChain.nativeCurrency,
              },
            ]);

            // After adding the chain, attempt to switch to it
            try {
              console.log("Chain added successfully, now switching to it...");
              await (currentProvider as any).send(
                "wallet_switchEthereumChain",
                [{ chainId: chainIdToHex(Number(id)) }],
              );
              toast.success(`Switched to ${targetChain.name}`);
              return targetChainId;
            } catch (retryError: any) {
              console.error(
                "Error switching to newly added chain:",
                retryError,
              );
              toast.success(
                `${targetChain.name} added. Please switch manually if needed.`,
              );
              return targetChainId;
            }
          } catch (addError) {
            console.error("Error adding chain:", addError);
            handleTransactionError(addError);
            toast.error(`Failed to add ${targetChain.name} to wallet`);
            return null;
          }
        } else {
          console.error("Error switching chain:", switchError);
          toast.error("Error occurred while switching chain.");
          handleTransactionError(switchError);
          return null;
        }
      }
    },
    [safeOpen],
  );

  const safeCallContract = useCallback(
    async ({
      name,
      method,
      args = [],
      chainId: reqChainId,
      showToast = true,
      _retryCount = 0,
    }: ContractCallParams & { _retryCount?: number }): Promise<
      [Error | null | true, ethers.Contract | ethers.ContractTransaction | null]
    > => {
      const currentProvider = providerRef.current;
      if (!currentProvider || !isConnected) {
        await safeOpen();
        if (showToast) toast.info("Please connect wallet.");
        return [true, null];
      }
      const currentChainId = String(chainIdRef.current);
      const targetChainIdForCall = reqChainId
        ? String(reqChainId)
        : currentChainId;

      if (currentChainId !== targetChainIdForCall) {
        if (showToast) {
          toast.info(
            `Requesting network switch to Chain ID: ${targetChainIdForCall}...`,
          );
        }
        const switchedToChainId = await switchChain(targetChainIdForCall);
        if (switchedToChainId !== targetChainIdForCall) {
          if (showToast) {
            toast.error(
              "Network switch failed or was not completed to the target chain.",
            );
          }
          return [true, null];
        }
      }

      const chainConfigForTarget = chains.find(
        (c) => String(c.id) === targetChainIdForCall,
      );
      if (!chainConfigForTarget) {
        const errorMsg = `Configuration not found for chain ID ${targetChainIdForCall}.`;
        if (showToast) toast.error(errorMsg);
        return [true, null];
      }

      const contractDetails =
        chainConfigForTarget.contracts[
          name as keyof typeof chainConfigForTarget.contracts
        ];
      if (
        !contractDetails ||
        !contractDetails.address ||
        !contractDetails.abi
      ) {
        const errorMsg = `Contract details (address/ABI) for '${String(
          name,
        )}' not found on chain ID ${targetChainIdForCall}`;
        console.error(errorMsg);
        if (showToast) {
          toast.error(
            `Contract '${String(name)}' configuration is missing for the target chain.`,
          );
        }
        return [new Error(errorMsg), null];
      }

      try {
        const freshProvider = providerRef.current;
        if (!freshProvider) throw new Error("Provider lost during call");
        const contractOnProvider = new Contract(
          contractDetails.address,
          contractDetails.abi,
          freshProvider,
        );
        let signer: ethers.Signer | null = null;
        try {
          signer = await freshProvider.getSigner();
        } catch {}

        const contractOnSigner = signer
          ? new Contract(contractDetails.address, contractDetails.abi, signer)
          : contractOnProvider;
        if (!method) {
          return [null, contractOnSigner];
        }
        if (typeof contractOnProvider[method] !== "function") {
          const errorMsg = `Method '${method}' does not exist on contract '${String(name)}'`;
          console.error(errorMsg);
          if (showToast) toast.error(`Contract method '${method}' not found`);
          return [new Error(errorMsg), null];
        }

        console.log(`Calling ${String(name)}.${method} with args:`, args);
        const fn = contractOnProvider.interface.getFunction(method);
        const mutability = fn?.stateMutability;
        const isRead = mutability === "view" || mutability === "pure";
        if (isRead) {
          const result = await contractOnProvider[method](...args);
          return [null, result as unknown as ethers.ContractTransaction];
        }
        if (!signer) throw new Error("Signer not available");
        const tx: ethers.ContractTransaction = await contractOnSigner[method](
          ...args,
        );
        return [null, tx];
      } catch (err: any) {
        if (err.code === "NETWORK_ERROR" && _retryCount < 3) {
          console.warn(
            `Network changed, retrying call to ${String(name)}.${method}...`,
          );
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return safeCallContract({
            name,
            method,
            args,
            chainId: reqChainId,
            showToast,
            _retryCount: _retryCount + 1,
          });
        }

        if (showToast) handleTransactionError(err);
        console.error(`Error calling ${String(name)}.${method}:`, err);
        return [err as Error, null];
      }
    },
    [isConnected, safeOpen, switchChain],
  );

  const updatePoints = useCallback(async (data: any): Promise<any> => {
    if (isUpdatingPointsRef.current) {
      console.warn(
        "Points update already in progress, skipping duplicate call",
      );
      return { skipped: true, reason: "already_updating" };
    }
    const now = Date.now();
    if (now - lastUpdatePointsTimeRef.current < 2000) {
      console.warn("Points update called too soon, throttling duplicate call");
      return { skipped: true, reason: "throttled" };
    }

    lastUpdatePointsTimeRef.current = now;
    isUpdatingPointsRef.current = true;
    try {
      const result = await completeTransaction(data);
      return result;
    } catch (error) {
      console.error("Error updating points:", error);
      throw error;
    } finally {
      isUpdatingPointsRef.current = false;
    }
  }, []);

  const safeSendTransaction = useCallback(
    async (
      transactionRequest: ethers.TransactionRequest,
    ): Promise<ethers.TransactionReceipt | null> => {
      const currentProvider = providerRef.current;
      if (!currentProvider || !isConnected) {
        toast.info("Please connect wallet to send transaction.");
        return null;
      }
      console.log("safeSendTransaction called with:", transactionRequest);
      try {
        const signer = await currentProvider.getSigner();
        if (!signer) throw new Error("Signer not available");

        const txResponse = await signer.sendTransaction(transactionRequest);

        toast.info("Transaction sent. Waiting for confirmation...", {
          toastId: "tx-pending",
        } as ToastOptions);

        const receipt = await txResponse.wait();
        toast.dismiss("tx-pending");
        if (receipt) {
          toast.success(
            `Transaction confirmed on block ${receipt.blockNumber}.`,
          );
        }

        return receipt;
      } catch (error) {
        toast.dismiss("tx-pending");
        handleTransactionError(error);
        return null;
      }
    },
    [isConnected],
  );

  const value = useMemo<WalletContextType>(
    () => ({
      address,
      chainId,
      isConnected,
      provider: ethersProvider,
      connect: async () => {
        await safeOpen();
      },
      safeOpen,
      switchChain,
      updatePoints,
      getChainConfig,
      safeCallContract,
      safeSendTransaction,
    }),
    [
      address,
      chainId,
      isConnected,
      ethersProvider,
      safeOpen,
      switchChain,
      updatePoints,
      getChainConfig,
      safeCallContract,
      safeSendTransaction,
    ],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};
